import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty, isNil, noop } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleModel, buildUsedTmvPricingWithOptionsPath } from 'client/data/models/vehicle-v2';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';
import {
  REPORT,
  PRICE_INFO,
  TRADE_IN_VALUE,
  REPORT_VIEW_TRACKING_EXTENDED_REPORT,
  APPRAISER_REPORT_CREATIVE_ID,
  NO_REPORT_VIEW_TRACKING,
} from 'site-modules/shared/constants/appraisal/appraisal';
import { EventToolbox } from 'client/utils/event-toolbox';
import {
  getAllTmvPrices,
  getAllTmvConditionPricing,
  getAllConditionTotalPrices,
  hasValidTmvData,
} from 'site-modules/shared/utils/appraisal/get-condition-pricing';
import { transformTmvConditions } from 'client/site-modules/shared/utils/appraiser/luckdragon-utils';
import { AppraisalReport } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/appraisal-report-step/appraisal-report-views/appraisal-report';
import { AppraisalReportExtended } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/appraisal-report-step/appraisal-report-views/appraisal-report-extended';
import { AppraisalReportErrorMessage } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/appraisal-report-step/appraisal-report-error-message';
import { TradeInAppraiserContext } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-context';

import './appraisal-report-step.scss';

/**
 * Appraisal report step.
 */
export class AppraisalReportStepUI extends Component {
  static propTypes = {
    stepId: PropTypes.string,
    // getVehicleData stays a required prop (not context) - stateToPropsConfig bindings below need it
    // as an ownProp, and luckdragon bindings can't read React context.
    getVehicleData: PropTypes.func.isRequired,
    toggleAppraiserDrawer: PropTypes.func,
    vehiclePhoto: PropTypes.string,
    zipCode: PropTypes.string,
    mileage: PropTypes.string,
    condition: PropTypes.string,
    vin: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({})),
    color: PropTypes.shape({}),
    isReportActive: PropTypes.bool,
    isMobile: PropTypes.bool,
    isExtendedReport: PropTypes.bool,
    emailReportStatus: PropTypes.string,
    stepNextId: PropTypes.string,
    isPurchase: PropTypes.bool,
    showReportExtendedPriceOnly: PropTypes.bool,
    sellingPrice: PropTypes.number,
    msrp: PropTypes.number,
    residualValue: PropTypes.number,
    isUsed: PropTypes.bool,
    selectedReportType: PropTypes.string,
    creativeId: PropTypes.string,
    tmvConditionsResponseData: PropTypes.shape({}),
    hasPartnerEquipment: PropTypes.bool,
    toggleScheduleAppointmentDrawer: PropTypes.func,
    isEscapeHatch: PropTypes.bool,
    licensePlateAndVinProps: PropTypes.shape({}),
    showAddEquipment: PropTypes.bool,
    validOffer: PropTypes.bool,
    isOfferLoading: PropTypes.bool,
    offerType: PropTypes.string,
    displayTradeInValues: PropTypes.bool,
    modsId: PropTypes.string,
  };

  static defaultProps = {
    stepId: REPORT,
    vehiclePhoto: '',
    toggleScheduleAppointmentDrawer: noop,
    toggleAppraiserDrawer: null,
    zipCode: '',
    mileage: '',
    vin: '',
    condition: '',
    options: [],
    color: {},
    isReportActive: false,
    isMobile: false,
    isExtendedReport: false,
    emailReportStatus: '',
    stepNextId: '',
    isPurchase: false,
    showReportExtendedPriceOnly: false,
    sellingPrice: 0,
    msrp: 0,
    residualValue: 0,
    isUsed: false,
    selectedReportType: TRADE_IN_VALUE,
    creativeId: APPRAISER_REPORT_CREATIVE_ID,
    tmvConditionsResponseData: {
      tmvConditions: null,
      responseId: undefined,
    },
    hasPartnerEquipment: false,
    modsId: '',
    /** Escape Hatch */
    isEscapeHatch: false,
    licensePlateAndVinProps: {},
    showAddEquipment: true,
    offerType: '',
    validOffer: false,
    isOfferLoading: false,
    displayTradeInValues: true,
  };

  state = {
    nationalPrices: {},
    regionalPrices: {},
    colorPrices: {},
    optionalPrices: {},
    totalPrices: {},
    mileagePrices: {},
    conditionPrices: {},
    isLoading: true,
    optionalPricesValues: [],
    allConditionPricing: {},
    isTradeInCalculated: false,
  };

  static getDerivedStateFromProps(props) {
    return {
      isLoading: isNil(props.tmvConditionsResponseData.tmvConditions),
    };
  }

  componentDidMount() {
    const {
      isReportActive,
      tmvConditionsResponseData: { tmvConditions },
    } = this.props;

    // Initialize for cases when component gets mounted after the step is active.
    if (isReportActive && !isNil(tmvConditions)) {
      this.initialize();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      isReportActive,
      tmvConditionsResponseData: { tmvConditions, responseId: tmvConditionsResponseId },
    } = this.props;
    const { isTradeInCalculated } = this.state;
    const hasTmvConditionChanged = tmvConditionsResponseId !== prevProps.tmvConditionsResponseData.responseId;

    if (isReportActive && !isNil(tmvConditions) && (!isTradeInCalculated || hasTmvConditionChanged)) {
      this.initialize();
    }

    /**
     * Reset "isTradeInCalculated" when value is changed but Step is "not active".
     * Covers case for:
     * 1) Instant Offer Step
     */
    if (!isReportActive && isTradeInCalculated && !isNil(tmvConditions) && hasTmvConditionChanged) {
      this.resetIsTradeInCalculated();
    }
  }

  getIsReportAvailable = () => {
    const {
      condition,
      tmvConditionsResponseData: { tmvConditions },
    } = this.props;
    const { isTradeInCalculated } = this.state;

    return hasValidTmvData(condition, tmvConditions) && isTradeInCalculated;
  };

  /**
   * Gets price list with values and static info.
   * @returns {Object[]}
   */
  getPriceList = () => {
    const {
      nationalPrices,
      regionalPrices,
      totalPrices,
      mileagePrices,
      colorPrices,
      conditionPrices,
      optionalPrices,
      optionalPricesValues,
    } = this.state;
    const { isExtendedReport } = this.props;

    return [
      { ...PRICE_INFO.NATIONAL_BASE, value: nationalPrices },
      { ...PRICE_INFO.OPTIONAL_EQUIPMENT, value: optionalPrices, values: optionalPricesValues },
      { ...PRICE_INFO.COLOR_ADJUSTMENT, value: colorPrices },
      { ...PRICE_INFO.REGIONAL_ADJUSTMENT, value: regionalPrices },
      { ...PRICE_INFO.MILEAGE_ADJUSTMENT, value: mileagePrices },
      { ...PRICE_INFO.CONDITION_ADJUSTMENT, value: conditionPrices },
      { title: isExtendedReport ? 'Value' : 'Value at Time of Build', value: totalPrices, isTotal: true },
    ];
  };

  getAppraisalReportEditControls = () => {
    const { color, options, hasPartnerEquipment, showAddEquipment } = this.props;
    return {
      showExteriorColor: Object.keys(color).length !== 0,
      showAddEquipment: showAddEquipment && hasPartnerEquipment && options.length === 0,
    };
  };

  static contextType = TradeInAppraiserContext;

  initialize = () => {
    const { stepId } = this.props;
    const { resetEmailReportStatus, toggleStepDone: onReportFormed } = this.context;
    resetEmailReportStatus();
    this.calculateTradeIn();
    // Use the actual stepId (e.g. INSTANT_OFFER when reused in the extended flow)
    onReportFormed(true, stepId);
  };

  resetIsTradeInCalculated = () => this.setState({ isTradeInCalculated: false });

  /**
   * Calculates trade-in values.
   */
  calculateTradeIn = () => {
    const {
      condition,
      options,
      creativeId,
      tmvConditionsResponseData: { tmvConditions },
      isEscapeHatch,
    } = this.props;
    const allTmvPrices = getAllTmvPrices(condition, tmvConditions, options);
    this.setState(
      {
        ...allTmvPrices,
        allConditionPricing: getAllTmvConditionPricing(tmvConditions, options),
        isTradeInCalculated: true,
      },
      () => {
        const { isExtendedReport, vehiclePhoto } = this.props;
        const { setAdditionalStepsData: updateStepsState } = this.context;
        const { totalPrices, certifiedUsedPrice, allConditionPricing, nationalPrices } = this.state;
        const hasTradeInValue = !!totalPrices[TRADE_IN_VALUE];
        const hasNationalPrices = !isEmpty(nationalPrices);
        const allConditionTotalPrices = getAllConditionTotalPrices(allConditionPricing);
        const updatedStepsData = {};
        if (hasTradeInValue) {
          updatedStepsData.allConditionTotalPrices = allConditionTotalPrices;
        }
        if (isExtendedReport) {
          const actualCreativeId = !isEmpty(totalPrices) ? creativeId : `${creativeId}-no-tmv`;
          EventToolbox.fireTrackAction(REPORT_VIEW_TRACKING_EXTENDED_REPORT(actualCreativeId));
          updatedStepsData.appraisalEmailData = {
            ...this.state,
            certifiedUsedPrice,
            vehiclePhoto,
            tmvConditions,
            isReportAvailable: this.getIsReportAvailable(),
          };
          if (isEscapeHatch) {
            // Make sure that appraisal email is sent for the second chance experience (i.e. escape hatch).
            // This email relies on appraisalEmailData (defined/saved above).
            updatedStepsData.isEmailDataReady = true;
          }
        } else if (!hasTradeInValue || !hasNationalPrices) {
          EventToolbox.fireTrackAction(NO_REPORT_VIEW_TRACKING(creativeId));
        }
        updateStepsState(updatedStepsData);
      }
    );
  };

  displayReport = () => {
    const {
      getVehicleData,
      sellingPrice,
      msrp,
      residualValue,
      isPurchase,
      isUsed,
      vehiclePhoto,
      color,
      condition,
      mileage,
      zipCode,
    } = this.props;
    const {
      handlePayoffChange: onPayoffChange,
      handlePaymentLimitsLoading: onPaymentLimitsLoading,
      changeStep,
    } = this.context;

    const { totalPrices } = this.state;

    return (
      <AppraisalReport
        getVehicleData={getVehicleData}
        changeStep={changeStep}
        onPayoffChange={onPayoffChange}
        onPaymentLimitsLoading={onPaymentLimitsLoading}
        priceList={this.getPriceList()}
        totalPrices={totalPrices}
        sellingPrice={sellingPrice}
        msrp={msrp}
        residualValue={residualValue}
        isPurchase={isPurchase}
        isUsed={isUsed}
        vehiclePhoto={vehiclePhoto}
        color={color}
        condition={condition}
        mileage={mileage}
        zipCode={zipCode}
        displayTradeInValues={this.props.displayTradeInValues}
        {...this.getAppraisalReportEditControls()}
      />
    );
  };

  displayExtendedReport = () => {
    const {
      getVehicleData,
      selectedReportType,
      stepId,
      stepNextId,
      creativeId,
      zipCode,
      color,
      options,
      condition,
      mileage,
      vin,
      emailReportStatus,
      isMobile,
      showReportExtendedPriceOnly,
      modsId,
      /** Escape Hatch */
      isEscapeHatch,
      licensePlateAndVinProps,
      offerType,
    } = this.props;
    const { changeStep, setAdditionalStepsData: updateStepsState, resetEmailReportStatus } = this.context;

    const { totalPrices, allConditionPricing } = this.state;

    return (
      <AppraisalReportExtended
        getVehicleData={getVehicleData}
        changeStep={changeStep}
        updateStepsState={updateStepsState}
        totalPrices={totalPrices}
        allConditionPricing={allConditionPricing}
        selectedReportType={selectedReportType}
        stepId={stepId}
        stepNextId={stepNextId}
        creativeId={creativeId}
        zipCode={zipCode}
        color={color}
        options={options}
        condition={condition}
        mileage={mileage}
        vin={vin}
        emailReportStatus={emailReportStatus}
        resetEmailReportStatus={resetEmailReportStatus}
        isMobile={isMobile}
        showReportPricesOnly={showReportExtendedPriceOnly}
        modsId={modsId}
        {...this.getAppraisalReportEditControls()}
        /** Escape Hatch */
        isEscapeHatch={isEscapeHatch}
        licensePlateAndVinProps={licensePlateAndVinProps}
        offerType={offerType}
      />
    );
  };

  render() {
    const { isLoading, isTradeInCalculated } = this.state;
    const {
      getVehicleData,
      isExtendedReport,
      creativeId,
      vin,
      isReportActive,
      toggleAppraiserDrawer,
      isEscapeHatch,
      validOffer,
      isOfferLoading,
    } = this.props;

    const { makeName, modelName, year } = getVehicleData();
    const isReportAvailable = this.getIsReportAvailable();

    const renderedReport = isExtendedReport ? this.displayExtendedReport() : this.displayReport();

    const displayAvailableReport = isReportAvailable ? (
      <div data-tracking-parent={creativeId}>
        <div
          className={classNames('appraisal-report-step', {
            'mx-md-1_5': isEscapeHatch,
          })}
        >
          {renderedReport}
        </div>
      </div>
    ) : (
      <Fragment>
        {isReportActive && isTradeInCalculated && (
          <AppraisalReportErrorMessage
            getVehicleData={getVehicleData}
            vin={vin}
            isExtendedReport={isExtendedReport}
            toggleAppraiserDrawer={toggleAppraiserDrawer}
            validOffer={validOffer}
            isOfferLoading={isOfferLoading}
          />
        )}
      </Fragment>
    );

    return (
      <LoadingSpinner isLoading={isLoading}>
        {isLoading ? (
          <div className="display-2">
            Calculating appraisal for your {year} {makeName} {modelName}!
          </div>
        ) : (
          displayAvailableReport
        )}
      </LoadingSpinner>
    );
  }
}

export const stateToPropsConfig = {
  vehiclePhoto: bindToPath(({ getVehicleData }) => {
    const { styleId } = getVehicleData();
    return styleId ? `styles.${styleId}.photo` : '';
  }, VehicleModel),
  tmvConditionsResponseData: bindToPath(
    ({ getVehicleData }) => {
      const { styleId } = getVehicleData();
      return styleId ? buildUsedTmvPricingWithOptionsPath(styleId) : '';
    },
    VehicleModel,
    transformTmvConditions
  ),
};

export const AppraisalReportStep = connectToModel(AppraisalReportStepUI, stateToPropsConfig);
